import React from "react";

/**
 * Recursively parses inline formatting markers and returns React elements.
 *   **text**  → <strong> (bold)
 *   _text_    → <em>     (italic)
 *   ~~text~~  → <u>      (underline)
 * Handles nested combinations: e.g. ~~**word**~~ renders as underlined-bold.
 */
const applyInlineFormats = (text, keyPrefix = "") => {
  if (!text) return null;

  const parts = text.split(/(\*\*[^*]+\*\*|~~[^~]+~~|_[^_]+_)/g);

  // No markers found — return plain text
  if (parts.length === 1) return text;

  return parts.map((part, i) => {
    const key = `${keyPrefix}${i}`;

    if (part.startsWith("**") && part.endsWith("**"))
      return (
        <strong key={key}>
          {applyInlineFormats(part.slice(2, -2), key)}
        </strong>
      );

    if (part.startsWith("~~") && part.endsWith("~~"))
      return (
        <u key={key}>
          {applyInlineFormats(part.slice(2, -2), key)}
        </u>
      );

    if (part.startsWith("_") && part.endsWith("_"))
      return (
        <em key={key}>
          {applyInlineFormats(part.slice(1, -1), key)}
        </em>
      );

    return part || null;
  });
};

/**
 * Entry point — call this wherever option_text needs to be displayed.
 * Falls back to the legacy colon-bold convention for unformatted options.
 */
const renderOptionText = (text) => {
  if (!text) return null;

  const hasFormatting =
    text.includes("**") || text.includes("_") || text.includes("~~");

  if (hasFormatting) {
    return applyInlineFormats(text);
  }

  // Legacy: bold everything before the first colon
  const colonIndex = text.indexOf(":");
  if (colonIndex !== -1) {
    return (
      <>
        <strong>{text.slice(0, colonIndex)}</strong>
        {text.slice(colonIndex)}
      </>
    );
  }

  return text;
};

export default renderOptionText;
